import { ArrowDown, ArrowUp, X } from "lucide-react"
import { useTranslation } from "react-i18next"
import { Popover, PopoverContent, PopoverTrigger, cn } from "@nexus/ui"
import type { MyBrandKpiCatalogEntry } from "../lib/my-brand-kpi-catalog"
import type { DrilldownSortDirection } from "../stores/drilldownFilters.store"

interface MyBrandKpiOverflowMenuProps {
  ids: readonly string[]
  byId: Record<string, MyBrandKpiCatalogEntry>
  sortMode: string
  sortDirection: DrilldownSortDirection
  canRemove: boolean
  onToggleSort: (id: string) => void
  onRemove: (id: string) => void
}

/** KPIs that do not fit on the single-line pill row. Same sort and remove actions
 *  as a pill; drag-reorder stays on the pills that are still visible. */
export function MyBrandKpiOverflowMenu({
  ids,
  byId,
  sortMode,
  sortDirection,
  canRemove,
  onToggleSort,
  onRemove,
}: MyBrandKpiOverflowMenuProps) {
  const { t } = useTranslation()
  if (ids.length === 0) return null

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="my-brand-kpi-overflow"
          aria-label={t("drilldown.myBrandKpi.moreAria", {
            count: ids.length,
            defaultValue_one: "Show {{count}} more KPI",
            defaultValue_other: "Show {{count}} more KPIs",
          })}
          className="flex h-[34px] shrink-0 items-center justify-center rounded-[10px] border border-dashed border-border bg-background px-2 text-xs font-medium tabular-nums text-muted-foreground hover:bg-muted hover:text-foreground"
        >
          {t("drilldown.myBrandKpi.moreCount", "+{{count}}", { count: ids.length })}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="max-h-[320px] w-[240px] overflow-y-auto p-1">
        {ids.map((id) => {
          const entry = byId[id]
          if (!entry) return null
          const isActiveSort = sortMode === id
          const SortIcon = sortDirection === "desc" ? ArrowDown : ArrowUp
          const sortDirectionLabel = t(
            sortDirection === "desc" ? "drilldown.myBrandKpi.sortDescending" : "drilldown.myBrandKpi.sortAscending",
            sortDirection === "desc" ? "descending" : "ascending",
          )
          return (
            <div
              key={id}
              data-testid={`my-brand-kpi-overflow-item-${id}`}
              className="flex items-center rounded-md"
            >
              <button
                type="button"
                onClick={() => onToggleSort(id)}
                className="flex h-8 min-w-0 flex-1 items-center gap-2 rounded-md px-2 text-left text-[13px] font-medium text-foreground hover:bg-muted"
              >
                <span className="size-3 shrink-0 rounded-[4px]" style={{ background: entry.colorVar }} aria-hidden />
                <span className="truncate">{entry.label}</span>
              </button>
              <button
                type="button"
                data-testid={`my-brand-kpi-overflow-${id}-sort`}
                aria-label={t("drilldown.myBrandKpi.sortBy", "Sort by {{label}}, currently {{direction}}", {
                  label: entry.label,
                  direction: sortDirectionLabel,
                })}
                onClick={() => onToggleSort(id)}
                style={isActiveSort ? { color: entry.colorVar } : undefined}
                className={cn(
                  "flex size-8 shrink-0 items-center justify-center rounded-md hover:bg-muted",
                  !isActiveSort && "text-muted-foreground",
                )}
              >
                <SortIcon className="size-3.5" />
              </button>
              <button
                type="button"
                data-testid={`my-brand-kpi-overflow-${id}-remove`}
                aria-label={t("drilldown.myBrandKpi.remove", "Remove {{label}}", { label: entry.label })}
                disabled={!canRemove}
                onClick={() => onRemove(id)}
                className={cn(
                  "flex size-8 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground",
                  !canRemove && "cursor-not-allowed opacity-40 hover:bg-transparent hover:text-muted-foreground",
                )}
              >
                <X className="size-3.5" />
              </button>
            </div>
          )
        })}
      </PopoverContent>
    </Popover>
  )
}
